type StructuredRecord = Record<string, unknown>;

type HistoryApplySourceInput = {
  /** Linha do historico como veio do ultimo `fetchHistory` — pode estar defasada. */
  entry: {
    id: string;
    transcript: string | null;
    structured: unknown;
    summary: string | null;
  };
  /** Estado vivo do painel de transcricao, atualizado pela extracao em curso. */
  live: {
    selectedHistoryId: string | null;
    transcript: string | null;
    structured: unknown;
    summary: string | null;
    aiStatus: string | null;
  };
};

type HistoryApplySourceResult = {
  source: "entry" | "live";
  transcript: string;
  structured: StructuredRecord;
  summary: string | null;
  isProcessing: boolean;
};

const asStructured = (value: unknown): StructuredRecord =>
  value && typeof value === "object" ? (value as StructuredRecord) : {};

const hasKeys = (value: StructuredRecord): boolean => Object.keys(value).length > 0;

/**
 * A lista do historico so e refetchada na hidratacao e quando a maquina volta a
 * "ready". Uma extracao disparada pela revisao atualiza o painel mas nao a linha,
 * entao aplicar direto pelo `entry` gravaria os campos vazios de antes. Aqui o
 * estado vivo vence campo a campo quando ele descreve o mesmo item.
 */
export const resolveHistoryApplySource = ({
  entry,
  live,
}: HistoryApplySourceInput): HistoryApplySourceResult => {
  const isSelected = live.selectedHistoryId === entry.id;

  const entryStructured = asStructured(entry.structured);
  const liveStructured = asStructured(live.structured);

  if (!isSelected) {
    return {
      source: "entry",
      transcript: entry.transcript ?? "",
      structured: entryStructured,
      summary: entry.summary,
      isProcessing: false,
    };
  }

  const useLiveStructured = hasKeys(liveStructured);
  const useLiveSummary = Boolean(live.summary?.trim());
  const useLiveTranscript = Boolean(live.transcript?.trim());

  return {
    source: useLiveStructured || useLiveSummary ? "live" : "entry",
    transcript: useLiveTranscript ? (live.transcript as string) : entry.transcript ?? "",
    structured: useLiveStructured ? liveStructured : entryStructured,
    summary: useLiveSummary ? live.summary : entry.summary,
    isProcessing: live.aiStatus === "processing",
  };
};
